import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUIStore } from '@/stores'
import { Button } from './Button'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
  // EP-80 #13: the bar is pinned to the bottom of the content area (a fixed bar
  // + a scroll spacer) so it stays visible while a long list scrolls — on BOTH
  // mobile and desktop. List pages scroll inside `<main overflow-auto>`, so a
  // trailing inline/sticky element sits below the fold on long lists; `fixed`
  // keeps it on screen. On desktop the bar is offset past the sidebar
  // (`lg:left-16/64`, tracking the collapse state) so it doesn't underlap it.
  // Opt out (false) for embedded/non-list contexts (side-by-side panels,
  // component showcases) — then it renders inline.
  stickyOnMobile?: boolean
  // EP-80 #14: total result count across all pages (e.g. 51). When provided, the
  // bar shows a summary ("51 rows · 3 pages · 20 each") and stays visible even on
  // a single page (so the count is always shown). `pageSize` (per_page, e.g. 20)
  // enriches the summary; omit it for the short form.
  totalItems?: number
  pageSize?: number
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  stickyOnMobile = true,
  totalItems,
  pageSize,
}: PaginationProps) {
  const { t } = useTranslation('common')
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)

  // Jump-to-page input (used when there are many pages). Kept in local state so
  // typing doesn't navigate on every keystroke — commit on Enter/blur.
  const [jumpValue, setJumpValue] = useState(String(currentPage))
  useEffect(() => {
    setJumpValue(String(currentPage))
  }, [currentPage])

  // Tell the page action bar that a bar rests on top of it, so it can drop its
  // own top border: the divider marks where the bar STACK begins, and drawing it
  // on both bars cut a line through the middle of the stack. Cleared on unmount
  // so a page without pagination gets its divider back.
  const showsBar = stickyOnMobile && (totalPages > 1 || (typeof totalItems === 'number' && totalItems > 0))
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--pagination-bar-present', showsBar ? '1' : '0')
    return () => root.style.setProperty('--pagination-bar-present', '0')
  }, [showsBar])

  const hasSummary = typeof totalItems === 'number' && totalItems > 0
  const hasControls = totalPages > 1

  // Nothing to render: no results to summarise AND only a single page.
  if (!hasSummary && !hasControls) return null

  // Rows and pages each take their own plural form in EN ("1 row · 1 page").
  const rows = hasSummary ? t('pagination.rows', { count: totalItems }) : ''
  const summary = hasSummary
    ? pageSize
      ? t('pagination.summary', { rows, count: totalPages, perPage: pageSize })
      : t('pagination.summary_short', { rows, count: totalPages })
    : null

  const goToJump = () => {
    const n = Number.parseInt(jumpValue, 10)
    if (Number.isNaN(n)) {
      setJumpValue(String(currentPage))
      return
    }
    const clamped = Math.min(totalPages, Math.max(1, n))
    setJumpValue(String(clamped))
    if (clamped !== currentPage) onPageChange(clamped)
  }

  const pageBtn = (page: number) => (
    <Button
      key={page}
      variant={currentPage === page ? 'primary' : 'ghost'}
      size="sm"
      className="min-w-8 px-1 sm:min-w-9 sm:px-2 tabular-nums"
      onClick={() => onPageChange(page)}
      aria-current={currentPage === page ? 'page' : undefined}
    >
      {page}
    </Button>
  )

  // Many pages → first two + a jump-to-page input + last two, e.g.
  // `< 1 2 [input] 6 7 >`. Few pages (≤5) → all buttons.
  const useJumpInput = totalPages > 5

  const controls = hasControls ? (
    <div className="flex flex-shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        shape="circle"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={t('pagination.previous')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {useJumpInput ? (
        <>
          {pageBtn(1)}
          {pageBtn(2)}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={totalPages}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            onBlur={goToJump}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                goToJump()
              }
            }}
            aria-label={t('pagination.go_to_page')}
            className="input input-bordered input-sm w-12 px-1 text-center tabular-nums"
          />
          {pageBtn(totalPages - 1)}
          {pageBtn(totalPages)}
        </>
      ) : (
        Array.from({ length: totalPages }, (_, i) => pageBtn(i + 1))
      )}

      <Button
        variant="ghost"
        size="sm"
        shape="circle"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={t('pagination.next')}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  ) : null

  // Inner row: summary (left) + controls (right); centred when only one exists.
  const inner = (
    <div
      className={`flex w-full items-center gap-3 ${
        summary && controls ? 'justify-between' : 'justify-center'
      } ${className}`}
    >
      {summary && (
        <span
          className="min-w-0 truncate text-xs tabular-nums text-base-content/60 sm:text-sm"
          data-testid="pagination-summary"
        >
          {summary}
        </span>
      )}
      {controls}
    </div>
  )

  if (!stickyOnMobile) return inner

  // EP-125 Q-1(a) stack: when a page also has a PageHeaderActions bottom action
  // bar, this bar must sit exactly on top of it. It reads the bar's MEASURED
  // height from --page-action-bar-height, published by PageHeaderActions, which
  // resolves to 0px when there is no bar (including at sm and up, where the bar
  // is display:none). This replaces a per-page prop that hardcoded
  // `calc(3.5rem + env(safe-area-inset-bottom))` — a constant ~16px taller than
  // the real bar, which is what left a gap between the two.
  const stackedBottom = 'bottom-[var(--page-action-bar-height,0px)] sm:bottom-0'

  return (
    <>
      {/* Spacer: reserves this bar's own height so the last list row isn't hidden
          behind it. Clearance for the page action bar underneath is MainLayout's
          job (<main> pads by --page-action-bar-height), so it isn't added twice. */}
      <div aria-hidden className="h-[calc(3.5rem+env(safe-area-inset-bottom))]" />
      {/* Fixed bar pinned to the bottom of the content area. Mobile: full width,
          resting on the page action bar when there is one.
          Desktop (lg+): offset past the sidebar (16/64 = collapsed/expanded) so
          it aligns with the main content and never underlaps the sidebar.
          z-30 sits below the sidebar backdrop (z-40) so the mobile drawer covers it.
          Bottom padding subtracts the action bar's height from the safe-area
          inset: with no bar (variable 0px, incl. desktop) it is the full inset,
          and with a bar underneath the subtraction goes negative and max()
          floors it at 0.5rem — so the inset is never paid for twice, which is
          what opened a gap between the two bars. */}
      <div
        data-testid="pagination-bar"
        className={`fixed inset-x-0 z-30 flex items-center border-t border-base-200 bg-base-100/95 px-4 py-2 pb-[max(calc(env(safe-area-inset-bottom)_-_var(--page-action-bar-height,0px)),0.5rem)] backdrop-blur ${stackedBottom} ${
          sidebarCollapsed ? 'lg:left-16' : 'lg:left-64'
        }`}
      >
        {inner}
      </div>
    </>
  )
}
